import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { EmptyState, ErrorState } from './states';
import { Skeleton } from './skeleton';

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[] | undefined;
  /** Query state, passed straight through from TanStack Query. */
  loading?: boolean;
  error?: boolean;
  errorMessage?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  /** Makes rows navigable. Rows become keyboard-reachable when set. */
  onRowClick?: (row: TData) => void;
  /** Row identity, so React does not key on the array index. */
  getRowId?: (row: TData) => string;
  initialSorting?: SortingState;
  className?: string;
}

/**
 * The one table in the codebase.
 *
 * Every previous `<table>` was a bare element with the states left to the page
 * author, which is how Accounts and Audit ended up rendering nothing at all
 * while loading and an empty `<tbody>` when a query failed. Loading, empty and
 * error are arguments here, so forgetting one is not possible.
 */
export function DataTable<TData>({
  columns,
  data,
  loading = false,
  error = false,
  errorMessage,
  emptyTitle,
  emptyDescription,
  onRowClick,
  getRowId,
  initialSorting = [],
  className,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });

  if (error) {
    return <ErrorState description={errorMessage ?? 'The request failed.'} />;
  }

  if (loading) {
    return (
      <div className={cn('rounded-lg border border-border', className)}>
        <div className="space-y-3 p-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className={cn('rounded-lg border border-border bg-card', className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="bg-muted/40">
              {group.headers.map((header) => {
                const sortable = header.column.getCanSort();
                const direction = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    // Announced so a screen reader knows the column is sorted
                    // and which way, not only that a button exists.
                    aria-sort={
                      direction === 'asc'
                        ? 'ascending'
                        : direction === 'desc'
                          ? 'descending'
                          : undefined
                    }
                  >
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        // `uppercase` is repeated from the `<th>` on purpose:
                        // Tailwind's preflight sets `text-transform: none` on
                        // `button`, so without it a sortable column header is
                        // cased differently from a fixed one.
                        className="inline-flex items-center gap-1 rounded uppercase tracking-wide transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {direction === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : direction === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn(onRowClick && 'cursor-pointer hover:bg-accent/60')}
              {...(onRowClick
                ? {
                    onClick: () => onRowClick(row.original),
                    // A clickable row that only responds to a mouse is
                    // unreachable during an incident run from the keyboard.
                    tabIndex: 0,
                    role: 'button',
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(row.original);
                      }
                    },
                  }
                : {})}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export type { ColumnDef, SortingState };
